import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowsClockwise, Plugs, BookOpenText } from "@phosphor-icons/react";
import { CardShell, AppNotConfigured, fmtWhen } from "./shared";

export default function XeroCard({ item, isAdmin, onChange }) {
  const conn = item?.connection;
  const [busy, setBusy] = useState("");
  const [ref, setRef] = useState(null);
  const [settings, setSettings] = useState(conn?.settings || {});
  const [open, setOpen] = useState(false);

  useEffect(() => { setSettings(conn?.settings || {}); }, [conn]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await API.post("/integrations/xero/connect", { back: "/settings?tab=integrations" });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Xero sign-in");
      setBusy("");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Xero? Existing links to Xero invoices are kept; nothing new will sync.")) return;
    setBusy("disconnect");
    try { await API.post("/integrations/xero/disconnect"); toast.success("Xero disconnected"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
    finally { setBusy(""); }
  };

  const loadRef = async () => {
    if (ref) return;
    try { const { data } = await API.get("/integrations/xero/reference"); setRef(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load Xero accounts"); }
  };

  const save = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await API.put("/integrations/xero/settings", patch); toast.success("Xero settings saved"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const sync = async () => {
    setBusy("sync");
    try { const { data } = await API.post("/integrations/xero/sync"); toast.success(`Checked ${data.checked} Xero invoices · ${data.marked_paid} marked paid`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try { const { data } = await API.post("/integrations/xero/test"); toast.success(`Connected to ${data.name} (${data.country}, ${data.currency})`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(""); }
  };

  return (
    <CardShell title="Xero" icon={<BookOpenText size={20} weight="duotone" className="text-sky-600" />} item={item} testid="integration-xero">
      {!conn ? (
        <>
          <AppNotConfigured item={item} testid="xero-app-missing" />
          {isAdmin && (
            <Button size="sm" onClick={connect} disabled={busy === "connect"} className="gap-1" data-testid="xero-connect-btn">
              <Plugs size={14} /> {busy === "connect" ? "Opening Xero…" : "Connect Xero"}
            </Button>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span data-testid="xero-org-name">Organisation: <b className="text-foreground">{conn.account?.name}</b></span>
            <span>Last sync: {fmtWhen(conn.last_sync_at)}</span>
            <span>Connected by {conn.connected_by}</span>
          </div>
          {isAdmin && (
            <>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={sync} disabled={!!busy} className="gap-1" data-testid="xero-sync-btn"><ArrowsClockwise size={13} className={busy === "sync" ? "animate-spin" : ""} /> Pull paid status now</Button>
                <Button size="sm" variant="outline" onClick={test} disabled={!!busy} data-testid="xero-test-btn">Test connection</Button>
                <Button size="sm" variant="ghost" onClick={() => { setOpen((o) => !o); if (!open) loadRef(); }} data-testid="xero-settings-toggle">{open ? "Hide settings" : "Sync settings"}</Button>
                <Button size="sm" variant="ghost" className="text-red-600 ml-auto" onClick={disconnect} disabled={!!busy} data-testid="xero-disconnect-btn">Disconnect</Button>
              </div>
              {open && (
                <div className="rounded-md border border-border p-3 grid gap-3 sm:grid-cols-2" data-testid="xero-settings">
                  <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                    {[["push_invoices", "Push invoices when sent"], ["push_payments", "Push payments when paid"], ["push_contacts", "Keep contacts in sync"], ["pull_status", "Pull paid status every 15 min"]].map(([k, label]) => (
                      <label key={k} className="flex items-center justify-between gap-2 text-xs rounded border border-border px-2 py-1.5">
                        <span>{label}</span>
                        <Switch checked={settings[k] !== false} onCheckedChange={(v) => save({ [k]: v })} data-testid={`xero-toggle-${k}`} />
                      </label>
                    ))}
                  </div>
                  <div>
                    <Label className="text-xs">Bank account for payments</Label>
                    <Select value={settings.bank_account_id || ""} onValueChange={(v) => { const a = (ref?.bank_accounts || []).find((x) => x.id === v); save({ bank_account_id: v, bank_account_name: a?.name || "" }); }}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="xero-bank-select"><SelectValue placeholder={ref ? "Choose a bank account" : "Loading…"} /></SelectTrigger>
                      <SelectContent>{(ref?.bank_accounts?.length ? ref.bank_accounts : (settings.bank_account_id ? [{ id: settings.bank_account_id, name: settings.bank_account_name || settings.bank_account_id }] : [])).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.code ? ` (${a.code})` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">Card, bank-transfer and Direct Debit payments are recorded here.</p>
                  </div>
                  <div>
                    <Label className="text-xs">VAT rate for invoice lines</Label>
                    <Select value={settings.tax_type || "OUTPUT2"} onValueChange={(v) => save({ tax_type: v })}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="xero-tax-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{(ref?.tax_rates?.length ? ref.tax_rates : [{ tax_type: "OUTPUT2", name: "20% (VAT on Income)", rate: 20 }]).map((t) => <SelectItem key={t.tax_type} value={t.tax_type}>{t.name}{t.rate != null ? ` — ${t.rate}%` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">Lines are sent VAT-exclusive; Xero adds VAT at this rate. Invoices without VAT use “No VAT”.</p>
                  </div>
                  <div>
                    <Label className="text-xs">Sales account</Label>
                    <Select value={settings.sales_account_code || "200"} onValueChange={(v) => save({ sales_account_code: v })}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="xero-sales-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{(ref?.sales_accounts?.length ? ref.sales_accounts : [{ code: "200", name: "Sales" }]).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Create invoices as</Label>
                    <Select value={settings.invoice_status || "AUTHORISED"} onValueChange={(v) => save({ invoice_status: v })}>
                      <SelectTrigger className="mt-1 h-8 text-xs" data-testid="xero-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="AUTHORISED">Approved (awaiting payment)</SelectItem><SelectItem value="DRAFT">Draft</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {(conn.account?.organisations || []).length > 1 && (
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Xero organisation</Label>
                      <Select value={conn.account?.xero_tenant_id} onValueChange={(v) => save({ xero_tenant_id: v })}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid="xero-org-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{conn.account.organisations.map((o) => <SelectItem key={o.tenant_id} value={o.tenant_id}>{o.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </CardShell>
  );
}
